<template>
  <div class="grid grid-cols-12 gap-4">
    <div class="col-span-12">
      <div class="grid grid-cols-12 gap-4 pt-1">
        <div class="col-span-12">
          <div class="border-b border-gray-200">
            <nav class="flex space-x-6 px-4" aria-label="Tabs">
            <div :class="[(currentRoute === 'subscribers') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']" @click="select('subscribers')">
              <div class="flex mt-6">
                <div style="font-size: 14px" v-if="company.subType !== 'private-company-limited-by-guarantee-without-shares'">Subscribers</div>
                <div style="font-size: 14px" v-else>Members</div>
                <span class="bg-gray text-white px-2 py-1 rounded-sm text-xs bg-primary mb-1 mx-1 ">
                  {{subscribersTotal}}
                </span>
              </div>
            </div>
            <div :class="[(currentRoute === 'directors') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']" @click="select('directors')">
              <div class="flex mt-6">
                <div style="font-size: 14px">Directors</div>
                <span class="bg-gray text-white px-2 py-1 rounded-sm text-xs bg-primary mx-1 mb-1">
                  {{(directorsTotal)}}
                </span>
              </div>
            </div>
            <div :class="[(currentRoute === 'secretary') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']" @click="select('secretary')">
              <div class="flex mt-6">
                <div style="font-size: 14px">Secretary</div>
                <span class="bg-gray text-white px-2 py-1 rounded-sm text-xs bg-primary mx-1 mb-1">
                  {{(secretaryTotal)}}
                </span>
              </div>
            </div>
            <div v-if="company.subType !== 'private-company-limited-by-guarantee-without-shares'" :class="[(currentRoute === 'shares') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']" @click="select('shares')">
              <div class="flex mt-6">
                <div style="font-size: 14px">Share Information</div>
              </div>
            </div>
            <div :class="[(currentRoute === 'address') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']" @click="select('address')">
              <div class="flex mt-6">
                <div style="font-size: 14px">Address</div>
              </div>
            </div>
            <div :class="[(currentRoute === 'documents') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']" @click="select('documents')">
              <div class="flex mt-6">
                <div style="font-size: 14px">Documents</div>
                <span class="bg-gray text-white px-2 py-1 rounded-sm text-xs bg-primary mx-1 mb-1">
                  {{company.documents.length}}
                </span>
              </div>
            </div>
            <div :class="[(currentRoute === 'filing-history') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']" @click="select('filing-history')">
              <div class="flex mt-6">
                <div style="font-size: 14px">Filing History</div>
              </div>
            </div>
            </nav>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12">
          <div class="grid grid-cols-12 gap-4">
            <CompanySubscribers v-if="currentRoute === 'subscribers'"/>
            <CompanyDirectors v-if="currentRoute === 'directors'"/>
            <CompanySecretary v-if="currentRoute === 'secretary'"/>
            <CompanyShares :brn="company.brn" v-if="currentRoute === 'shares' && company.subType !== 'private-company-limited-by-guarantee-without-shares'"/>
            <CompanyAddress :is-filed="getFilingCompliance?.data?.formCompliance?.complianceForForm18" :brn="company.brn" v-if="currentRoute === 'address'"/>
            <CompanyDocuments :brn="company.brn" :documents="company.documents" v-if="currentRoute === 'documents'"/>
            <FilingHistory :brn="company.brn" v-if="currentRoute === 'filing-history'"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useBusinessFileStore } from "@/stores/business-file";
import CompanySubscribers
  from "@/domains/home/services/registry/business/files/tabs/company_data/CompanySubscribers.vue";
import CompanyDirectors from "@/domains/home/services/registry/business/files/tabs/company_data/CompanyDirectors.vue";
import CompanyShares from "@/domains/home/services/registry/business/files/tabs/company_data/CompanyShares.vue";
import CompanyAddress from "@/domains/home/services/registry/business/files/tabs/company_data/CompanyAddress.vue";
import CompanyDocuments from "@/domains/home/services/registry/business/files/tabs/company_data/CompanyDocuments.vue";
import FilingHistory from "@/domains/home/services/registry/business/files/FilingHistory.vue";
import CompanySecretary from "@/domains/home/services/registry/business/files/tabs/company_data/CompanySecretary.vue";

const props = defineProps<{ company: any }>();

const fileStore = useBusinessFileStore();

const getFilingCompliance = computed(() => fileStore.filing_compliance);

const currentRoute = ref("subscribers");
const subscribersTotal = ref(0);
const directorsTotal = ref(0);
const secretaryTotal = ref(0);

onMounted(() => {
  fileStore.fetchCompanySubscribers(props.company.brn);
  fileStore.fetchCompanyDirectors(props.company.brn);
  fileStore.fetchCompanySecretary(props.company.brn);
  fileStore.fetchFilingCompliance(props.company.brn);
});

watch(() => fileStore.subscribers, (data) => {
  subscribersTotal.value = data?.data?.content?.length ?? 0;
});

watch(() => fileStore.directors, (data) => {
  directorsTotal.value = data?.data?.content?.length ?? 0;
});

watch(() => fileStore.secretary, (data) => {
  if (data) {
    secretaryTotal.value = 1;
  }
});

const select = (route: string) => {
  currentRoute.value = route;
}
</script>

